import type { WorklogRules, WorklogUnit } from '@forecastify/shared';
import { CheckboxField, Select, Stepper } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { Field } from '../card';
import { SettingsCard } from './settings-shell';
import { SettingsPage } from './settings-page';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Worklog szabályok — `markup.html` 2045–2104.                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE FORM C13 OWED.** C13 shipped the rules table, the enforcement, the endpoint and its
 * guard, and `chunks.md` handed the *form* to C16 — *"until it lands, a workspace runs on the
 * mockup's defaults and changing them means calling the endpoint"*. This is it. Nothing here invents
 * a rule; every one is enforced by `enforceRules` already.
 *
 * ⚠️ **The blue banner at the top is the mockup's, and it is not decoration.** *"Worklogot csak
 * feladathoz lehet rögzíteni. Ez nem kapcsolható ki."* It states a constraint that has no control
 * because it is not configurable — `worklogs.task_id` is `not null` — and saying so is what stops
 * somebody looking for the switch.
 */

export interface WorklogSectionProps {
  readonly rules: WorklogRules;
  readonly draft: WorklogRules;
  readonly canManage: boolean;
  readonly saving: boolean;
  readonly serverError: string | null;
  readonly onDraft: (patch: Partial<WorklogRules>) => void;
  readonly onReset: () => void;
  readonly onSave: () => void;
}

function unitLabel(t: Translate, unit: WorklogUnit): string {
  /* One `t()` per branch with a literal key. */
  if (unit === 'min15') return t('worklog.unit.min15');
  if (unit === 'min30') return t('worklog.unit.min30');
  return t('worklog.unit.decimal');
}

/* ⚠️ `unitNote` went with the hint lines — item 66a. Its three sentences restated the picker's own
   value, and the catalogue keys stay for now: `i18n:check` reports an orphan rather than failing,
   and deleting words is a separate sweep from deleting the code that read them. */

export function WorklogSection(props: WorklogSectionProps) {
  const t = useT();
  const { rules, draft, canManage } = props;

  const dirty =
    draft.unit !== rules.unit ||
    draft.maxPerDay !== rules.maxPerDay ||
    draft.backdateDays !== rules.backdateDays ||
    draft.backdateNoLimit !== rules.backdateNoLimit ||
    draft.lockDay !== rules.lockDay;

  /* ⚠️ The endpoint's own bounds, restated so the Save is blocked rather than the round trip wasted:
   * `maxPerDay` is `> 0 && <= 24`, `backdateDays` is `>= 1`. */
  const maxInvalid = !(draft.maxPerDay > 0 && draft.maxPerDay <= 24);
  const backdateInvalid = !draft.backdateNoLimit && !(draft.backdateDays >= 1);

  /* ⚠️ An explicit Save, not the 15 Sep auto-save — `general-section.tsx` carries the argument.
     Disabled until something changed, while a number is one the endpoint would refuse, and while
     a write is in flight. */
  const saveAction = !canManage
    ? undefined
    : {
        label: props.saving ? t('common.saving') : t('common.save'),
        icon: 'check' as const,
        disabled: !dirty || maxInvalid || backdateInvalid || props.saving,
        onSelect: props.onSave,
      };
  const discardAction = !canManage
    ? undefined
    : {
        label: t('common.discard'),
        disabled: !dirty || props.saving,
        onSelect: props.onReset,
      };

  return (
    <div data-screen="settings-worklog" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.worklog')}
        width="form"
        action={saveAction}
        secondaryAction={discardAction}
      >
        <div className="flex flex-col gap-4">
          {/* ⚠️ The mockup's *"worklog only attaches to a task"* banner is removed — asked for on
          14 Sep. It stated a constraint with no control beside it, at the top of a screen that is
          otherwise entirely controls, so it was read once and skipped for ever after. The rule it
          describes has not changed; it is enforced server-side either way. */}
          <SettingsCard title={t('settings.worklog.title')}>
            <div className="mt-3 flex flex-col gap-3">
              {/**
               * ┌─────────────────────────────────────────────────────────────────┐
               * │ ⚠️⚠️ THE `note` LINES ARE GONE — item 66a.                      │
               * └─────────────────────────────────────────────────────────────────┘
               *
               * Each field carried a sentence restating what the control above it had just been set
               * to — *"15 perces léptékben"* under a picker reading *15 perc*, *"legfeljebb 8 óra
               * naponta"* under a stepper reading `8`. A hint that repeats its own control is noise
               * that makes the card twice as tall and teaches people to stop reading hints.
               *
               * ⚠️⚠️ **`error` STAYS ON EVERY ONE OF THEM.** A refusal is not a hint: it says what is
               * wrong with what somebody just typed, which the control cannot say for itself.
               */}
              <Field label={t('settings.worklog.unit')} tourId="settings-worklog-unit">
                <Select
                  value={draft.unit}
                  disabled={!canManage}
                  onChange={(event) => {
                    props.onDraft({ unit: event.target.value as WorklogUnit });
                  }}
                  options={(['min15', 'min30', 'decimal'] as const).map((unit) => ({
                    value: unit,
                    label: unitLabel(t, unit),
                  }))}
                />
              </Field>

              <Field
                tourId="settings-worklog-maxPerDay"
                label={t('settings.worklog.maxPerDay')}
                error={maxInvalid ? t('settings.worklog.maxInvalid') : undefined}
              >
                <div className="flex items-center gap-2">
                  {/* ⚠️ An explicit `aria-label`: `Field`'s own label wraps a ROW of controls here, so it
                  names none of them on its own. */}
                  <Stepper
                    min={1}
                    max={24}
                    step={0.5}
                    aria-label={t('settings.worklog.maxPerDay')}
                    decrementLabel={t('common.decrease')}
                    incrementLabel={t('common.increase')}
                    disabled={!canManage}
                    value={String(draft.maxPerDay)}
                    className="w-[110px]"
                    onChange={(next) => {
                      props.onDraft({ maxPerDay: Number(next) });
                    }}
                  />
                  <span className="text-muted-foreground text-[12px]">
                    {t('settings.worklog.hoursPerDay')}
                  </span>
                </div>
              </Field>

              <Field
                tourId="settings-worklog-backdate"
                label={t('settings.worklog.backdate')}
                error={backdateInvalid ? t('settings.worklog.backdateInvalid') : undefined}
              >
                <div className="flex items-center gap-2">
                  <Stepper
                    min={1}
                    aria-label={t('settings.worklog.backdate')}
                    decrementLabel={t('common.decrease')}
                    incrementLabel={t('common.increase')}
                    /* ⚠️ Disabled by the checkbox, which is the mockup's own coupling — a number beside
                     * "no limit" would be two answers to one question. */
                    disabled={!canManage || draft.backdateNoLimit}
                    value={String(draft.backdateDays)}
                    className="w-[110px]"
                    onChange={(next) => {
                      props.onDraft({ backdateDays: Number(next) });
                    }}
                  />
                  <span className="text-muted-foreground text-[12px]">
                    {t('settings.worklog.daysBack')}
                  </span>
                  <span className="flex-1" />
                  {/* 26 Oct 2026 · the text beside the box, as the mockup draws it — a bare `Checkbox`
                      gives its label to screen readers only, so the box read as unexplained. */}
                  <CheckboxField
                    checked={draft.backdateNoLimit}
                    disabled={!canManage}
                    onCheckedChange={(next) => {
                      props.onDraft({ backdateNoLimit: next });
                    }}
                    label={t('settings.worklog.noLimit')}
                  />
                </div>
              </Field>

              <Field label={t('settings.worklog.lockDay')} tourId="settings-worklog-lock">
                <div className="flex items-center gap-2">
                  {/**
                   * ⚠️ **1–28 and a "no lock" option, and the ceiling is the mockup's own.** A lock day
                   * of 30 never arrives in February, so the previous month would never close in some
                   * years and close on a different date in others. `rules.ts` records the same.
                   */}
                  {/**
                   * ⚠️⚠️ **THE WIDTH GOES ON A WRAPPER, NEVER ON `Select` ITSELF — and that is why the
                   * label was clipped.** `Select`'s outer element is `inline-flex w-full` and the
                   * `className` it takes lands on the BUTTON inside it, so `w-[190px]` sized the
                   * button while its wrapper went on claiming the whole row and squeezed *"után zárul
                   * az előző hónap"* off the end of it. Same trap the settings filter strips hit on
                   * 11 Sep; `select.tsx` carries the note.
                   */}
                  <span className="w-[190px] shrink-0">
                    <Select
                      value={draft.lockDay === null ? '' : String(draft.lockDay)}
                      aria-label={t('settings.worklog.lockDay')}
                      disabled={!canManage}
                      onChange={(event) => {
                        props.onDraft({
                          lockDay: event.target.value === '' ? null : Number(event.target.value),
                        });
                      }}
                      options={[
                        { value: '', label: t('settings.worklog.noLock') },
                        ...Array.from({ length: 28 }, (_, index) => ({
                          value: String(index + 1),
                          label: t('settings.worklog.dayOfMonth', { day: String(index + 1) }),
                        })),
                      ]}
                    />
                  </span>
                  <span className="text-muted-foreground min-w-0 text-[12px]">
                    {t('settings.worklog.lockDayAfter')}
                  </span>
                </div>
              </Field>

              {/* ⚠️ The amber *"nobody can log to last month after the 5th"* panel is removed —
              asked for on 14 Sep, same argument as the banner above: it restated the consequence of
              the number sitting right beside it. The rule still applies; `isMonthLocked` enforces
              it and the time screen names it when somebody hits it, which is the moment it is
              worth saying. */}
            </div>
          </SettingsCard>
        </div>

        {props.serverError !== null && (
          <p
            role="alert"
            className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-[12.5px]"
          >
            {props.serverError}
          </p>
        )}
      </SettingsPage>
    </div>
  );
}
